/**
 * Degree / Program field updates for two-step (and always-visible) lead forms.
 *
 * Selecting Degree or Program must only update field state — never submit-level
 * validation, loader, or API. Program stays required; validation runs on
 * explicit Submit via RHF handleSubmit + resolver.
 */
import type {
  FieldValues,
  Path,
  PathValue,
  UseFormClearErrors,
  UseFormRegisterReturn,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";
import { scheduleAfterPaint } from "@/helpers/scheduleAfterPaint";

/**
 * Update a lead text/checkbox field without blocking the keystroke, then
 * re-validate that field after paint so Controller `fieldState.error` and
 * `formState.errors` stay in sync (clear as soon as the value is valid).
 *
 * Degree/Program must keep using setLeadFormDegreeValue / setLeadFormProgramValue.
 */
export function setLeadFormInputValue<T extends FieldValues>(
  setValue: UseFormSetValue<T>,
  trigger: UseFormTrigger<T>,
  name: Path<T>,
  value: PathValue<T, Path<T>>,
): void {
  setValue(name, value, {
    shouldDirty: true,
    shouldTouch: true,
    shouldValidate: false,
  });
  const fieldKey = String(name);
  if (fieldKey === "degree" || fieldKey === "program" || fieldKey === "programId") {
    return;
  }
  scheduleAfterPaint(() => {
    void trigger(name);
  });
}

/** Same live revalidation for native `register()` inputs (corporate and similar). */
export function bindLeadRegisterChange<T extends FieldValues>(
  registered: UseFormRegisterReturn,
  trigger: UseFormTrigger<T>,
  name: Path<T>,
): UseFormRegisterReturn {
  return {
    ...registered,
    onChange: (event) => {
      const result = registered.onChange(event);
      scheduleAfterPaint(() => {
        void trigger(name);
      });
      return result;
    },
  };
}

/**
 * Resolve the lead-check callback with this after stage-1 reveals Degree/Program.
 * handleSendOTP must stop here — otherwise `await cb()` never settles (stuck
 * "Submitting...") or OTP would fire before the user finishes stage-2.
 */
export const LEAD_CHECK_SKIP_OTP = { skipOtp: true as const };

type DegreeProgramValues = FieldValues & {
  degree?: string;
  program?: string;
};

export function setLeadFormDegreeValue<T extends DegreeProgramValues>(
  setValue: UseFormSetValue<T>,
  clearErrors: UseFormClearErrors<T>,
  degree: string,
  selectProgramPlaceholder: string,
): void {
  setValue("degree" as Path<T>, degree as T[Path<T>], {
    shouldDirty: true,
    shouldValidate: false,
  });
  setValue("program" as Path<T>, selectProgramPlaceholder as T[Path<T>], {
    shouldDirty: true,
    shouldValidate: false,
  });
  clearErrors("program" as Path<T>);
}

export function setLeadFormProgramValue<T extends DegreeProgramValues>(
  setValue: UseFormSetValue<T>,
  clearErrors: UseFormClearErrors<T>,
  program: string,
  selectProgramPlaceholder?: string,
): void {
  setValue("program" as Path<T>, program as T[Path<T>], {
    shouldDirty: true,
    shouldValidate: false,
  });
  const isPlaceholder =
    !program ||
    (selectProgramPlaceholder
      ? program === selectProgramPlaceholder
      : String(program).includes("Select"));
  if (!isPlaceholder) {
    clearErrors("program" as Path<T>);
  }
}
